/**
 * authApi — typed controller for the CUSTOMER auth gateway (ADR-003 D2/D8).
 *
 * The gateway lives at 127.0.0.1:9102 behind the host's nginx on
 * /api/auth/*. The SPA exchanges only display profile fields; the session is
 * opaque to JavaScript (HttpOnly SMARTDASH_SESSION cookie). The double-submit
 * CSRF token is JS-readable (SMARTDASH_CSRF cookie) and echoed on every
 * state-changing request.
 *
 * Endpoints (openapi/auth-api.yaml):
 *   POST /api/auth/login   {username, password}     → 200 {sessionId, csrfToken, user}; 401/423/429/400 → ApiError
 *   POST /api/auth/logout  X-SmartDash-CSRF header   → 200 {status:'ok'}; 401/403 → ApiError
 *   GET  /api/auth/me      (session cookie)         → 200 {user}; 401 → ApiError
 *
 * Employee/staff auth (ADR-003 D8) lives in staffAuthApi.ts.
 *
 * 401 from any backend call (here and via apiClient) is routed to the
 * AuthStore's unauthorized handler so the AuthGate can re-render login.
 */

import { endpoints, request } from './apiClient'
import { readCsrfCookie } from '../lib/cookies'
import { ApiError } from '@domain/types'
import {
  AuthApiError,
  type AuthUser,
  type CustomerLoginResult,
  mapAuthErrorCode,
} from '@domain/auth'

interface GatewayLoginResponse {
  readonly sessionId?: unknown
  readonly csrfToken?: unknown
  readonly user?: unknown
}

interface GatewayMeResponse {
  readonly user?: unknown
}

interface GatewayUser {
  readonly id?: unknown
  readonly username?: unknown
  readonly displayName?: unknown
  readonly role?: unknown
}

function assertUser(value: unknown, what: string): AuthUser {
  if (value === null || typeof value !== 'object') {
    throw new ApiError(502, 'INVALID_RESPONSE', `Auth gateway response is missing the ${what} object.`)
  }
  const obj = value as GatewayUser
  if (typeof obj.id !== 'number' || !Number.isFinite(obj.id)) {
    throw new ApiError(502, 'INVALID_RESPONSE', `Auth gateway response ${what}.id is missing or not a number.`)
  }
  if (typeof obj.username !== 'string' || typeof obj.displayName !== 'string' || typeof obj.role !== 'string') {
    throw new ApiError(502, 'INVALID_RESPONSE', `Auth gateway response ${what} is missing username/displayName/role.`)
  }
  return {
    id: obj.id,
    username: obj.username,
    displayName: obj.displayName,
    role: obj.role,
  }
}

/**
 * POST /api/auth/login {username, password}.
 *
 * Throws AuthApiError with a pre-classified reason on any failure so the
 * LoginPage can render the named state without re-mapping status codes.
 */
export async function login(username: string, password: string, signal?: AbortSignal): Promise<CustomerLoginResult> {
  try {
    const response = await request<GatewayLoginResponse>(endpoints.authLogin, {
      method: 'POST',
      body: { username, password },
      signal,
    })
    if (typeof response.sessionId !== 'string' || typeof response.csrfToken !== 'string') {
      throw new ApiError(502, 'INVALID_RESPONSE', 'Auth gateway login response is missing sessionId or csrfToken.')
    }
    return {
      sessionId: response.sessionId,
      csrfToken: response.csrfToken,
      user: assertUser(response.user, 'user'),
    }
  } catch (error) {
    throw rewrapAuthError(error)
  }
}

/**
 * POST /api/auth/logout with the double-submit CSRF header.
 *
 * Reads SMARTDASH_CSRF from cookies on every call (the gateway may have
 * rotated it; the cookie is the source of truth). 401 is treated as
 * "already logged out" and is swallowed — authStore.logout() wants the
 * local state cleared regardless of whether the gateway still had a
 * session to revoke.
 */
export async function logout(signal?: AbortSignal): Promise<void> {
  // Always clear local state first — even if the network call fails, the
  // user must be presented as logged out.
  const csrf = readCsrfCookie()
  if (csrf === null) {
    // No CSRF cookie → either never logged in or already cleared. Either way,
    // the gateway cannot accept the logout (it would 403 on missing CSRF);
    // tell the caller "done" so AuthGate clears the UI.
    return
  }
  try {
    await request<unknown>(endpoints.authLogout, {
      method: 'POST',
      headers: { 'X-SmartDash-CSRF': csrf },
      signal,
    })
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      // No session to revoke OR CSRF mismatch — the local state should be
      // cleared regardless.
      return
    }
    // Network failure on logout is non-fatal: cookies will expire server-side.
    // Surfacing it would leave the user staring at a spinner.
    return
  }
}

/**
 * GET /api/auth/me. Returns the current user or null on 401 (no session).
 * Other errors (network, 5xx) propagate as ApiError.
 */
export async function me(signal?: AbortSignal): Promise<AuthUser | null> {
  try {
    const response = await request<GatewayMeResponse>(endpoints.authMe, { signal })
    return assertUser(response.user, 'user')
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return null
    }
    throw error
  }
}

function rewrapAuthError(error: unknown): AuthApiError {
  if (error instanceof AuthApiError) return error
  if (error instanceof ApiError) {
    const reason = mapAuthErrorCode(error.status, error.code)
    return new AuthApiError(error.status, error.code, error.message, reason)
  }
  // Network failure or unexpected error — treat as unavailable. The LoginPage
  // surfaces "service unreachable" in that case.
  const message = error instanceof Error ? error.message : String(error)
  return new AuthApiError(0, 'UNREACHABLE', `Cannot reach the auth gateway — ${message}`, 'unavailable')
}
